// react
import { useMemo } from 'react';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui
import { DataGridPremium } from '@mui/x-data-grid-premium';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';

import { DateTime } from 'luxon';

const classifySignal = (rssi) => {
  if (rssi == null) return 'Unknown';
  if (rssi === 0) return 'Offline';
  if (rssi >= -60) return 'Excellent';
  if (rssi >= -70) return 'Good';
  if (rssi >= -80) return 'Fair';
  return 'Poor';
};

const classifyLqi = (lqi) => {
  if (lqi == null) return 'Unknown';
  if (lqi === 0) return 'Offline';
  if (lqi >= 35) return 'Excellent';
  if (lqi >= 25) return 'Good';
  if (lqi >= 15) return 'Fair';
  return 'Poor';
};

// const statusColor = {
//   Excellent: 'success',
//   Good: 'info',
//   Fair: 'warning',
//   Poor: 'error',
//   Offline: 'default',
//   Unknown: 'default',
// };

const parseLastActivityTs = (isoString) => {
  if (!isoString) return null;
  const dt = DateTime.fromISO(isoString, { setZone: true });
  return dt.isValid ? dt.toMillis() : null;
};

const SensorActivitiesDataGrid = ({
  sensorActivities = [],
  externalSensorActivities = [],
}) => {
  const navigate = useNavigate();

  const handleSensorActivityRowClick = (params) => {
    const row = params?.row;
    if (!row) return;

    // Internal sensors navigate to /sensors/:id
    if (!row.isExternal && row.SensorId) {
      navigate({ to: `/$accountId/sensors/${row.SensorId}` });
      return;
    }

    // External sensors: do nothing for now (or add your route)
    // if (row.isExternal && row.ExternalSensorId) {
    //   navigate({ to: `/$accountId/devices/${row.ExternalSensorId}` });
    // }
  };

  const rows = useMemo(() => {
    const now = DateTime.now();
    const cutoffTs = now.minus({ hours: 24 }).toMillis();

    const internalNormalized = (sensorActivities ?? []).map((s) => {
      const lastActivityTs = parseLastActivityTs(s?.LastActivity);

      return {
        ...s,
        isExternal: false,
        sensorKey: s?.SensorId != null ? String(s.SensorId) : null, // unified key for display/dedupe
        lastActivityTs,
        signalQuality: classifySignal(s?.ReceivedSignalStrengthIndication),
        linkQuality: classifyLqi(s?.LinkQualityIndication),
      };
    });

    const externalNormalized = (externalSensorActivities ?? []).map((s) => {
      const lastActivityTs = parseLastActivityTs(s?.LastActivity);

      return {
        ...s,
        isExternal: true,
        // provide a unified shape: external has no SensorType or LQI in your example
        SensorType: s?.SensorType ?? 'External',
        LinkQualityIndication: s?.LinkQualityIndication ?? null,

        sensorKey:
          s?.ExternalSensorId != null ? String(s.ExternalSensorId) : null,
        lastActivityTs,
        signalQuality: classifySignal(s?.ReceivedSignalStrengthIndication),
        linkQuality: classifyLqi(s?.LinkQualityIndication),
      };
    });

    const combined = [...internalNormalized, ...externalNormalized].filter(
      (r) => r.lastActivityTs != null && r.lastActivityTs >= cutoffTs,
    );

    // Dedupe:
    // - internal: SensorId
    // - external: ExternalSensorId
    // - fallback: GatewayId + LastActivity + isExternal
    // Keep newest lastActivityTs when duplicates happen.
    const byKey = new Map();

    for (const r of combined) {
      const key = r.isExternal
        ? r?.ExternalSensorId != null
          ? `external:${r.ExternalSensorId}`
          : `external:fallback:${r?.GatewayId ?? 'gw'}|${r?.LastActivity ?? 't'}`
        : r?.SensorId != null
          ? `sensor:${r.SensorId}`
          : `sensor:fallback:${r?.GatewayId ?? 'gw'}|${r?.LastActivity ?? 't'}`;

      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, r);
        continue;
      }

      const existingTs = existing?.lastActivityTs ?? 0;
      const currentTs = r?.lastActivityTs ?? 0;
      if (currentTs > existingTs) byKey.set(key, r);
    }

    return Array.from(byKey.values());
  }, [sensorActivities, externalSensorActivities]);

  const columns = useMemo(
    () => [
      {
        field: 'origin',
        headerName: 'Origin',
        width: 110,
        valueGetter: (_value, row) => (row?.isExternal ? 'External' : 'ReMoni'),
      },

      // unified id column (shows SensorId or ExternalSensorId)
      {
        field: 'sensorKey',
        headerName: 'Sensor ID',
        width: 140,
        valueGetter: (_value, row) =>
          row?.isExternal ? row?.ExternalSensorId : row?.SensorId,
      },

      // { field: 'GatewayId', headerName: 'Gateway', width: 140 },

      { field: 'SensorType', headerName: 'Type', width: 160 },

      // Hidden numeric sort field (stable + robust)
      // {
      //   field: 'lastActivityTs',
      //   headerName: 'Last Activity (ts)',
      //   type: 'number',
      //   hide: true,
      // },

      // Display column: computed from lastActivityTs at render time
      {
        field: 'lastActivityAgo',
        headerName: 'Last Activity',
        flex: 1,
        minWidth: 200,
        sortable: true,

        // Make sorting use the numeric timestamp
        valueGetter: (_value, row) => row?.lastActivityTs ?? 0,
        sortComparator: (a, b) => (a ?? 0) - (b ?? 0),

        // Show “x minutes ago”
        renderCell: (params) => {
          const ts = params?.row?.lastActivityTs;
          if (!ts) return '—';

          const rel = DateTime.fromMillis(ts).toRelative({
            base: DateTime.now(),
          });

          return rel ?? '—';
        },
      },

      {
        field: 'ReceivedSignalStrengthIndication',
        headerName: 'RSSI (dBm)',
        flex: 1,
      },

      // {
      //   field: 'signalQuality',
      //   headerName: 'Signal Status',
      //   width: 150,
      //   renderCell: (params) => {
      //     const v = params.value ?? 'Unknown';
      //     return <Chip label={v} size="small" color={statusColor[v]} />;
      //   },
      // },

      { field: 'LinkQualityIndication', headerName: 'LQI', width: 110 },

      // {
      //   field: 'linkQuality',
      //   headerName: 'Link Status',
      //   width: 150,
      //   renderCell: (params) => {
      //     const v = params.value ?? 'Unknown';
      //     return <Chip label={v} size="small" color={statusColor[v]} />;
      //   },
      // },
    ],
    [],
  );

  return (
    // <Box>
    //   <Typography variant="overline" gutterBottom>
    //     Sensor Activities (last 24h)
    //   </Typography>
    <DataGridPremium
      rows={rows}
      columns={columns}
      getRowId={(row) =>
        row?.isExternal
          ? `external-${row?.ExternalSensorId ?? `${row?.GatewayId ?? 'gw'}-${row?.LastActivity ?? Math.random()}`}`
          : `sensor-${row?.SensorId ?? `${row?.GatewayId ?? 'gw'}-${row?.LastActivity ?? Math.random()}`}`
      }
      getRowClassName={(params) =>
        params?.row?.isExternal ? 'row-external' : ''
      }
      pageSizeOptions={[10, 25, 50]}
      initialState={{
        pagination: { paginationModel: { pageSize: 25, page: 0 } },
        sorting: {
          sortModel: [{ field: 'lastActivityAgo', sort: 'desc' }], // newest first
        },
      }}
      showToolbar
      slots={{
        toolbar: DataGridToolbar,
      }}
      slotProps={{
        toolbar: {
          title: 'Sensor Activities (last 24h)',
          showExport: false,
          showDivider: false,
        },
      }}
      onRowClick={handleSensorActivityRowClick}
      sx={{ cursor: 'pointer' }}
    />
    // </Box>
  );
};

export default SensorActivitiesDataGrid;
